import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Home, Phone, UserCircle } from 'lucide-react';
import Navbar from '@/components/smartpos/Navbar';
import Footer from '@/components/smartpos/Footer';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';

export default function PageNotFound() {
  const location = useLocation();

  useDocumentMeta({
    title: 'Página no encontrada | SmartPOS',
    description: 'La página que buscas no existe o ha sido movida.',
    canonicalPath: location.pathname,
  });

  // noindex for 404
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'noindex, nofollow');
    return () => {
      meta.setAttribute('content', 'index, follow, max-image-preview:large');
    };
  }, []);

  const { data: authData, isFetched } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      try {
        const user = await base44.auth.me();
        return { user, isAuthenticated: true };
      } catch (error) {
        return { user: null, isAuthenticated: false };
      }
    }
  });

  return (
    <div className="min-h-screen font-body bg-white flex flex-col relative overflow-hidden">
      <Navbar variant="light" />

      <main id="contenido-principal" className="flex-1 flex items-center justify-center px-6 pt-40 pb-32 relative">
        {/* Halos de marca sobre fondo claro */}
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[38rem] h-[38rem] rounded-full bg-brand-blue/10 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 right-0 w-[26rem] h-[26rem] rounded-full bg-brand-violet/10 blur-3xl" />

        <div className="relative max-w-xl w-full text-center">
          <p className="font-heading font-light text-6xl sm:text-8xl leading-none brand-gradient-text">404</p>
          <div className="h-px w-16 mx-auto mt-8 bg-gray-200" />

          <h1 className="mt-8 font-heading font-light text-2xl sm:text-3xl text-gray-900 text-balance">
            Página no encontrada
          </h1>
          <p className="mt-4 text-gray-500 leading-relaxed font-light">
            La dirección <span className="text-gray-800 break-all">{decodeURIComponent(location.pathname)}</span> no existe o ha sido movida.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/demo"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-bold text-white bg-brand-blue rounded-full hover:bg-[#0078d4] hover:scale-105 transition-all shadow-lg shadow-blue-500/20"
            >
              Pedir demo <ArrowRight size={16} />
            </Link>
            <Link
              to="/contacto"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-bold text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50 transition-all"
            >
              <Phone size={15} /> Contactar
            </Link>
          </div>

          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gray-800 transition-colors"
          >
            <Home size={14} /> Volver al inicio
          </Link>

          {isFetched && authData.isAuthenticated && authData.user?.role === 'admin' && (
            <div className="mt-12 p-4 rounded-xl bg-gray-50 border border-gray-100 text-left">
              <p className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-1">
                <UserCircle size={15} className="text-brand-blue shrink-0" />
                Nota para el administrador
              </p>
              <p className="text-sm text-gray-500 leading-relaxed font-light">
                Es posible que esta página aún no haya sido implementada. Puedes solicitarla en el chat.
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}